import DateUtils, { APP_TIMEZONE } from "./DateUtils";

class FormatUtils {
  DEFAULT_LNG = "cs";
  DEFAULT_DECIMALS = 1;

  DEFAULT_NUM_FORMAT = new Intl.NumberFormat(this.DEFAULT_LNG, { maximumFractionDigits: this.DEFAULT_DECIMALS });

  DEFAULT_DECIMALS_PER_TYPE = {
    hour: 3,
    day: 2,
    "15minute": 2,
    month: 1,
    twoWeeks: 1,
    weeks: 1,
  };

  /**
   * Formats a number according to the provided options or defaults.
   * @param {number} value - The number to format.
   * @param {Object} options - The formatting options.
   * @param {number} options.maxDecimals - The maximum number of decimal places.
   * @param {string} options.unit - The unit of the number.
   * @param {string} options.language - The language to use for formatting.
   * @param {Object} options.format - The format to use for the number.
   * @returns {string} The formatted number.
   */
  formatNumber(value, options = {}) {
    const { maxDecimals, unit, language, format } = options;

    const number = value.toNumber ? value.toNumber() : value;

    let numberFormat;
    if (format) {
      numberFormat = format;
    } else if (Object.keys(options).length === 0) {
      numberFormat = this.DEFAULT_NUM_FORMAT;
    } else {
      numberFormat = new Intl.NumberFormat(language || this.DEFAULT_LNG, {
        maximumFractionDigits: maxDecimals ?? this.DEFAULT_DECIMALS,
        unit,
        style: unit ? "unit" : "decimal",
      });
    }

    return numberFormat.format(number);
  }

  /**
   * Formats a number as kilowatt-hours (kWh).
   * @param {number} value - The number to format.
   * @param {Object} options - The formatting options.
   * @param {number} options.maxDecimals - The maximum number of decimal places.
   * @param {string} options.unit - The unit of the number.
   * @param {string} options.language - The language to use for formatting.
   * @param {Object} options.format - The format to use for the number.
   * @returns {string} The formatted number with " kWh" appended.
   */
  formatKwh(value, options = {}) {
    return this.formatNumber(value, options) + " kWh";
  }

  /**
   * Formats a date (calendar day) in the app timezone ({@link APP_TIMEZONE}).
   * @param {Date|string|number} date
   * @returns {string}
   */
  formatDate(date) {
    return new Date(date).toLocaleDateString(this.DEFAULT_LNG, { timeZone: APP_TIMEZONE });
  }

  /**
   * Formats a date as ISO calendar day `YYYY-MM-DD` in the app timezone ({@link APP_TIMEZONE}).
   * @param {Date|string|number} date
   * @returns {string}
   */
  formatIsoDate(date) {
    const jsDate = new Date(date);
    if (Number.isNaN(jsDate.getTime())) {
      return "";
    }

    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: APP_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(jsDate);

    const get = (type) => parts.find((part) => part.type === type)?.value;
    return `${get("year")}-${get("month")}-${get("day")}`;
  }

  /**
   * Formats a date and time as a locale string.
   * @param {Date|string} date - The date to format.
   * @param {Object} options - The formatting options.
   * @param {boolean} options.seconds - Whether to include seconds in the output.
   * @returns {string} The formatted date and time.
   */
  formatDateTime(date, options = { seconds: true }) {
    return new Date(date).toLocaleString(this.DEFAULT_LNG, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: options.seconds ? "2-digit" : undefined,
    });
  }

  formatHours(date) {
    const jsDate = new Date(date);
    return `${jsDate.getHours()}:${jsDate.getMinutes().toString().padStart(2, "0")}`;
  }

  formatHoursFromFloat(hours) {
    const minutes = (hours % 1) * 60;
    return `${Math.floor(hours).toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  formatMonth(date) {
    const jsDate = new Date(date);
    const month = jsDate.getMonth();
    return DateUtils.MONTHS[month] + " " + jsDate.getFullYear();
  }

  formatDateRange(from, to) {
    const jsFrom = new Date(from);
    const jsTo = new Date(to);

    let dayPart = `${jsFrom.getDate()}. `;
    let monthPart = `${jsFrom.getMonth() + 1}. `;
    let yearPart = `${jsFrom.getFullYear()} `;

    const sameDate = jsFrom.getDate() === jsTo.getDate();
    const sameMonth = jsFrom.getMonth() === jsTo.getMonth();
    const sameYear = jsFrom.getFullYear() === jsTo.getFullYear();

    // If both dates are exactly the same, return just one formatted date
    if (sameDate && sameMonth && sameYear) {
      return `${dayPart}${monthPart}${yearPart}`;
    }

    // Check if the months are the same
    if (sameMonth && sameYear) {
      monthPart = "";
    }

    // Check if the years are the same
    if (sameYear) {
      yearPart = "";
    }

    return `${dayPart}${monthPart}${yearPart}- ${jsTo.getDate()}. ${jsTo.getMonth() + 1}. ${jsTo.getFullYear()}`;
  }

  normalizeString(string) {
    return string
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[ .,-/+&]/g, "_")
      .toLowerCase();
  }
}

export default new FormatUtils();
